import React, { createContext, useState, useEffect, useContext } from 'react';
import adminAuthService from '../services/adminAuthService';

// Create the admin auth context
export const AdminAuthContext = createContext();

/**
 * Admin Authentication Context Provider
 * Manages admin-specific authentication state and provides auth-related functions
 */
export const AdminAuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize admin auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if admin is already authenticated
        if (adminAuthService.isAuthenticated()) {
          // Get admin user from localStorage first for immediate UI update
          const storedUser = adminAuthService.getUser();
          if (storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
          }

          // Then fetch fresh admin user data from API
          try {
            const currentUser = await adminAuthService.getCurrentUser();
            setUser(currentUser);
            setIsAuthenticated(true);
          } catch (err) {
            // If API call fails, admin might be logged out or token expired
            console.warn('Failed to get current admin user, logging out:', err);
            adminAuthService.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (err) {
        console.error('Admin auth initialization error:', err);
        setError(err.message || 'Authentication error');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login admin user
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   */
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminAuthService.login(email, password);
      // The backend returns data in response.data.data structure
      if (response.data && response.data.data && response.data.data.user) {
        // Verify user is an admin
        if (response.data.data.user.role !== 'admin') {
          throw new Error('User is not an admin');
        }
        
        setUser(response.data.data.user);
        setIsAuthenticated(true);
        return response.data.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Admin login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout current admin user
   */
  const logout = async () => {
    setLoading(true);
    
    try {
      await adminAuthService.logout();
    } catch (err) {
      console.error('Admin logout error:', err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  /**
   * Update current admin user's password
   */
  const updatePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await adminAuthService.updatePassword(currentPassword, newPassword);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || 'Password update failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin: true, // Always true for admin auth context
    login,
    logout,
    updatePassword,
    clearError: () => setError(null)
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

// Custom hook for using admin auth context
export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthContextProvider');
  }
  return context;
};
